import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ComboBox } from 'components/inputs/ComboBox';
import { CurrentTagsList } from './CurrentTagsList';
import { ConfirmAddCallout } from './ConfirmAddCallout';
import './TagsEditor.less';
import { DropDownOption } from 'components/inputs/DropDownOption';
import { TagData } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Tagging';
import {
  getTagsByObjectID,
  getCategoriesAndTagsForTree,
  addTag,
  removeTag,
  checkUserCanCreateTags,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Tagging/TaggingService';
import { sanitizeHtml } from 'utils/StringUtils';

export interface TagsEditorProps {
  objectId: string;
  objectType: string;
  objectShortType: string;
}

const WATERMARK = 'Type Here To Add Tags';

export function TagsEditor({ objectId, objectType, objectShortType }: TagsEditorProps) {
  const [tags, setTags] = useState<string[]>();
  const [selectedTags, setSelectedTags] = useState<TagData[]>([]);
  const [filter, setFilter] = useState('');
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const popupLinkRef = useRef(null);

  const updateSelectedTags = useCallback(async () => {
    setSelectedTags(await getTagsByObjectID({ entityID: objectId, entityTypeName: objectType }));
  }, [objectId, objectType]);

  const getTags = useCallback(() => {
    if (tags) {
      // filter selected tags
      const result = tags.filter((tag: string) => selectedTags.map((s: TagData) => s.Tag).indexOf(tag) === -1);

      // filter tags by input value
      return result.filter((tag: string) => tag.indexOf(filter) !== -1);
    } else {
      return [];
    }
  }, [filter, selectedTags, tags]);

  useEffect(() => {
    async function fetchData() {
      await updateSelectedTags();
      setTags(await (await getCategoriesAndTagsForTree({ ownerEntityTypeName: objectType })).map(t => t.Name));
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTagClick = useCallback(
    async (tagName: string) => {
      await addTag({ objectID: objectId, objectType, objectShortType, tag: tagName });
      await updateSelectedTags();
    },
    [objectId, objectShortType, objectType, updateSelectedTags]
  );

  const removeTagClick = useCallback(
    async (value: string) => {
      await removeTag({ objectID: objectId, objectType, tag: value });
      await updateSelectedTags();
    },
    [objectId, objectType, updateSelectedTags]
  );

  const onTyping = useCallback(
    async (e: React.KeyboardEvent<{}>) => {
      if (e.key === 'Enter') {
        const canCreate = await checkUserCanCreateTags();
        if (tags?.indexOf(filter) === -1 && canCreate) {
          setPopupOpen(true);
        }
      }
    },
    [filter, tags]
  );

  const createTag = useCallback(async () => {
    await addTag({ objectID: objectId, objectType, objectShortType, tag: filter });
    await updateSelectedTags();
    setPopupOpen(false);
  }, [filter, objectId, objectShortType, objectType, updateSelectedTags]);

  const onFieldChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeHtml(e.target.value);
    setFilter(value);
  }, []);

  const onClear = useCallback(() => {
    setFilter('');
  }, []);

  return (
    <div className="dpc-tags-editor">
      {getTags() && (
        <div className="dpc-tags-editor__combo" ref={popupLinkRef}>
          <ComboBox onChange={onFieldChange} onClear={onClear} onKeyUp={onTyping} placeholder={WATERMARK}>
            {getTags().map((value: string, i: number) => (
              <DropDownOption key={i} index={i} onChosen={addTagClick} isChosen={false}>
                {value}
              </DropDownOption>
            ))}
          </ComboBox>
        </div>
      )}
      <CurrentTagsList currentTags={selectedTags} removeTagClick={removeTagClick} />
      <ConfirmAddCallout
        linkRef={popupLinkRef}
        popupOpen={popupOpen}
        closePopup={useCallback(() => {
          setPopupOpen(false);
        }, [])}
        onSubmit={createTag}
        newTagName={filter}
      />
    </div>
  );
}
